import React from 'react';
import clsx, { ClassValue } from 'clsx';
import { useSortable } from '@dnd-kit/react/sortable';
import { ExperimentMetric, ExperimentMetricResults, ExperimentMetricType, StatisticallySignificant } from 'apps/experiments/src/types';
import { MetricResult, useExperimentsStore } from 'apps/experiments/src/store';
import { ButtonReact } from 'common/components/button';
import { useTranslation } from 'common/i18n/hooks';
import { ButtonSize, ButtonTheme } from 'common/components/button/constants';
import { useExperimentPageContext } from 'apps/experiments/src/components/context';
import { getFinalDisplayOptions } from 'common/report/insights/charts/util';
import { eventPropertyDefinitionsStore } from 'common/report/stores/property-definitions';
import { useMetrics } from 'common/contexts/react/metrics-resource-context';
import { formatMetricNumber } from 'common/util';
import { TooltipReact } from 'common/components/tooltip';
import { TooltipTextAlign } from 'common/components/tooltip/types';
import { SvgIconReact } from 'common/components/svg-icon';
import { PageTrackingEvent, trackExperimentPageAction, TrackingEventSource } from 'apps/experiments/src/components/tracking';
import { useExperimentMetrics } from 'apps/experiments/src/components/hooks/metrics';
import { SORTABLE_METRIC_TYPE } from '../const';
import styles from './_row.module.styl';
import { ExperimentPageSummaryMetricsCategoryChart } from './_chart';
type ExperimentPageSummaryMetricsCategoryRowProps = {
  index: number;
  metric: ExperimentMetric;
  type: ExperimentMetricType;
  variantNames: string[];
  absMax: number;
  results?: MetricResult;
};
const CONTROL_KEY_ERROR = "Control key not found in results";
export function ExperimentPageSummaryMetricsCategoryRow(props: ExperimentPageSummaryMetricsCategoryRowProps) {
  const {
    t
  } = useTranslation();
  const experimentState = useExperimentsStore(state => state.experimentState);
  const currentMetric = useExperimentsStore(state => state.currentMetric);
  const {
    selectMetric,
    deleteMetric
  } = useExperimentMetrics();
  const metrics = useMetrics();
  const {
    canEdit
  } = useExperimentPageContext();
  const {
    ref,
    isDragging
  } = useSortable({
    id: props.metric.id,
    index: props.index,
    type: SORTABLE_METRIC_TYPE,
    group: props.type
  });
  const handleRemoveMetric = React.useCallback((id: string) => {
    deleteMetric(id);
  }, [deleteMetric]);
  const percentFormatter = new Intl.NumberFormat("default", {
    style: "percent",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  });
  const displayOptions = props.metric.definition ? getFinalDisplayOptions(props.metric.definition, eventPropertyDefinitionsStore, metrics) : {};
  const formatMetric = (value: number) => {
    var _displayOptions$prefi, _displayOptions$suffi;
    return "".concat((_displayOptions$prefi = displayOptions.prefix) !== null && _displayOptions$prefi !== void 0 ? _displayOptions$prefi : "").concat(formatMetricNumber(value)).concat((_displayOptions$suffi = displayOptions.suffix) !== null && _displayOptions$suffi !== void 0 ? _displayOptions$suffi : "");
  };
  const isActive = (currentMetric === null || currentMetric === void 0 ? void 0 : currentMetric.id) === props.metric.id;
  const handleSelectMetric = React.useCallback(() => {
    selectMetric(isActive ? undefined : props.metric, TrackingEventSource.ExperimentPageMetrics);
  }, [isActive, props.metric, selectMetric]);
  const innerPropsForResults = React.useCallback((resultsMap: Record<string, ExperimentMetricResults>, variantName: string) => {
    const results = resultsMap === null || resultsMap === void 0 ? void 0 : resultsMap[variantName];
    const {
      control,
      controlFormat,
      treatment,
      treatmentFormat,
      lift,
      liftConfidence,
      pValue,
      statisticallySignificant
    } = results !== null && results !== void 0 ? results : {
      control: NaN,
      controlFormat: "number",
      treatment: NaN,
      treatmentFormat: "number",
      lift: NaN,
      liftConfidence: NaN,
      pValue: NaN,
      statisticallySignificant: StatisticallySignificant.NO
    };
    const liftNode = lift ? <>{percentFormatter.format(lift)}</> : <>&mdash;</>;
    const controlNode = control ? <>{controlFormat === "percent" ? percentFormatter.format(control) : formatMetric(control)}</> : <>&mdash;</>;
    const treatmentNode = treatment ? <>{treatmentFormat === "percent" ? percentFormatter.format(treatment) : formatMetric(treatment)}</> : <>&mdash;</>;
    const chartNode = <ExperimentPageSummaryMetricsCategoryChart absMax={props.absMax} lift={lift} liftConfidence={liftConfidence} pValue={pValue} statisticallySignificant={statisticallySignificant} />;
    return {
      wrapperClassName: clsx(isActive, {
        [styles.good]: statisticallySignificant === StatisticallySignificant.YES_POSITIVE,
        [styles.bad]: statisticallySignificant === StatisticallySignificant.YES_NEGATIVE
      }),
      liftNode,
      controlNode,
      treatmentNode,
      chartNode
    };
  }, [isActive, props.absMax]);
  const innerPropsForError = React.useCallback((error: Error) => {
    let errorMessage = t("experiments.main.metrics.error");
    if (error.message === CONTROL_KEY_ERROR) {
      errorMessage = t("experiments.main.metrics.control-error");
    }
    const liftNode = <span className={styles.liftError}>
          <SvgIconReact className={styles.error} icon='warning' />
          <TooltipReact delayIn={250} textAlign={TooltipTextAlign.Start} maxWidth='200px' text={errorMessage} />
        </span>;
    const controlNode = <>&mdash;</>;
    const treatmentNode = <>&mdash;</>;
    const chartNode = <ExperimentPageSummaryMetricsCategoryChart absMax={props.absMax} lift={NaN} liftConfidence={NaN} pValue={NaN} statisticallySignificant={StatisticallySignificant.NO} />;
    return {
      liftNode,
      controlNode,
      treatmentNode,
      chartNode
    };
  }, [isActive, props.absMax]);
  const rowInnerProps = React.useMemo(() => props.variantNames.map(variantName => {
    if (props.results instanceof Error) {
      return innerPropsForError(props.results);
    } else {
      var _props$results;
      return innerPropsForResults((_props$results = props.results) !== null && _props$results !== void 0 ? _props$results : {}, variantName);
    }
  }), [props.results, props.absMax, props.variantNames, innerPropsForResults, innerPropsForError]);
  React.useEffect(() => {
    if (props.results instanceof Error) {
      trackExperimentPageAction({
        event: PageTrackingEvent.MetricError,
        experiment: experimentState,
        props: {
          'error message': props.results.message,
          dataGroupId: props.metric.dataGroupId,
          definition: props.metric.definition
        }
      });
    } else if (props.results) {
      trackExperimentPageAction({
        event: PageTrackingEvent.MetricLoaded,
        experiment: experimentState,
        props: {
          'variants - length': Object.values(props.results).length,
          dataGroupId: props.metric.dataGroupId,
          definition: props.metric.definition
        }
      });
    }
  }, [props.results]);
  return <div className={clsx(styles.rowContainer, {
    [styles.active]: isActive
  })} ref={ref} data-dragging={isDragging} onClick={() => handleSelectMetric()} data-sentry-component="ExperimentPageSummaryMetricsCategoryRow" data-sentry-source-file="_row.tsx">
      <StyledRow index={props.index} isActive={isActive} metricName={props.metric.name || t("query-builder.select-metric")} variantNames={props.variantNames} rowInnerProps={rowInnerProps} data-sentry-element="StyledRow" data-sentry-source-file="_row.tsx" />
      {canEdit && <div className={styles.toolbar}>
          <ButtonReact icon='trashcan' theme={ButtonTheme.Tertiary} size={ButtonSize.Inline} onClick={ev => {
        ev.stopPropagation();
        handleRemoveMetric(props.metric.id);
      }} />
        </div>}
    </div>;
}
type StyledRowProps = {
  index: number;
  metricName: string;
  variantNames: string[];
  isActive?: boolean;
  rowInnerProps: Array<{
    wrapperClassName?: ClassValue;
    liftNode?: React.ReactNode;
    controlNode?: React.ReactNode;
    treatmentNode?: React.ReactNode;
    chartNode?: React.ReactNode;
  }>;
};
function StyledRow(props: StyledRowProps) {
  var _props$rowInnerProps$;
  const multiVariant = props.variantNames.length > 1;
  return <div className={clsx(styles.row, styles.rowLayout, {
    [styles.active]: props.isActive
  }, !multiVariant ? (_props$rowInnerProps$ = props.rowInnerProps[0]) === null || _props$rowInnerProps$ === void 0 ? void 0 : _props$rowInnerProps$.wrapperClassName : undefined)} data-sentry-component="StyledRow" data-sentry-source-file="_row.tsx">
      <div className={clsx(styles.cell, multiVariant ? [styles.shortMetricCell, styles.shortMetricCellLayout] : [styles.metricCell, styles.metricCellLayout])}>
        <div className={styles.metricLabel} title={props.metricName}>{"".concat(props.index + 1, ". ").concat(props.metricName)}</div>
      </div>
      <div className={styles.rowInnerContainer}>
        {props.variantNames.map((variantName, index) => {
        var _props$rowInnerProps$2, _props$rowInnerProps$3, _props$rowInnerProps$4, _props$rowInnerProps$5, _props$rowInnerProps$6;
        return <div className={clsx(styles.rowInner, (_props$rowInnerProps$2 = props.rowInnerProps[index]) === null || _props$rowInnerProps$2 === void 0 ? void 0 : _props$rowInnerProps$2.wrapperClassName)} key={index}>
            {multiVariant && <div title={variantName} className={clsx(styles.cell, styles.variantCellLayout, {
            [styles.bordered]: !props.isActive && index < props.variantNames.length - 1
          })}>
                {variantName}
              </div>}
            <div className={clsx(styles.cell, styles.numberCell, styles.numberCellLayout, styles.goodBadCell, {
            [styles.bordered]: !props.isActive
          })}>
              {(_props$rowInnerProps$3 = props.rowInnerProps[index]) === null || _props$rowInnerProps$3 === void 0 ? void 0 : _props$rowInnerProps$3.liftNode}
            </div>
            <div className={clsx(styles.cell, styles.numberCell, styles.numberCellLayout, {
            [styles.bordered]: !props.isActive
          })}>
              {(_props$rowInnerProps$4 = props.rowInnerProps[index]) === null || _props$rowInnerProps$4 === void 0 ? void 0 : _props$rowInnerProps$4.controlNode}
            </div>
            <div className={clsx(styles.cell, styles.numberCell, styles.numberCellLayout, {
            [styles.bordered]: !props.isActive
          })}>
              {(_props$rowInnerProps$5 = props.rowInnerProps[index]) === null || _props$rowInnerProps$5 === void 0 ? void 0 : _props$rowInnerProps$5.treatmentNode}
            </div>
            <div className={clsx(styles.cell, styles.chartCellLayout, {
            [styles.bordered]: !props.isActive
          })}>
              {(_props$rowInnerProps$6 = props.rowInnerProps[index]) === null || _props$rowInnerProps$6 === void 0 ? void 0 : _props$rowInnerProps$6.chartNode}
            </div>
          </div>;
      })}
      </div>
    </div>;
}